// Dip Simple-arm PDP - usage-question buy module (the Simple theme, rolling out with the
// sitewide first-order offer).
// Replaces the size/frequency pickers with two usage questions (washes per week +
// load size) and assigns pack + cadence from the answers: rate = washes/week x load
// factor, pack = smallest whose cadence is 4+ weeks, cadence = floor(pack / rate)
// in weeks (weekly plans up to 12 weeks, month wording from 13 weeks). Plan of
// record: docs/dip-simple-laundry-pdp-plan.md; offer build docs/dip-simple-offer-rollout-build.md.
//
// The plan reveal is a PLAN BLOCK of radio cards (offer decision ruling 16, build phase 3;
// Charlie's review rounds 2026-09-06): Basic = this product on the matched pack, pre-selected;
// "Fewer deliveries" = the bigger pack at the same rate (a synthetic tier, on pages that turn
// `bump_card` on: laundry, MSC); a companion tier adds another product (dishwasher and fabcon:
// + laundry), every line itemised with its own cadence and roll-on price (ruling 15) and the
// tier's pound saving (ruling 13), companion lines de-selectable. The whole-routine bundle tier
// was tried and dropped (round 2: too much information). On the laundry page a "Do you use fabric
// conditioner? Yes / No" step before the plan adds the conditioner line, matched to the laundry
// pack and cadence, to every card. A multi-surface line brings its bottle + cloth on every surface
// (ruling 9, assets/dip-hardware.js).
//
// Everything money-bearing is computed from the data island exactly as dip-buy-box.js
// does. The welcome kit and its gift codes are GONE from order 1 (ruling 1, phase 2).

import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { initGallery } from '@theme/dip-gallery';
import '@theme/dip-tabs';
import '@theme/dip-quotes';
import { initStickyBar } from '@theme/dip-sticky-bar';
import {
  cadenceFor, planFor, basePack, bumpPack, cadenceLabel, variantFor, packsOf, companionBooking,
} from '@theme/dip-plan-engine';
// The first-order offer (assets/dip-offer.js): first-order price, the pound saving and the
// itemised roll-on line, from the island's `offer` object and each variant's `offer` flag.
import { priceLine, firstLabel, renewalShipMinor, rollOnLine } from '@theme/dip-offer';
// The multi-surface hardware (assets/dip-hardware.js): per-size bottle + cloth lines, the
// "Included free" line and the guard publication, from the island's `hardware` object.
import {
  hwActive, hwQty, hwLines, hwWorth, hwIncludedText, reconcileHardwareLines, publishHardwareCodes,
} from '@theme/dip-hardware';
// The Everything split test (docs/dip-everything-split-test.md): the visitor's arm, and the
// choice cards shared with the routine page.
import { withArm, tagCart, evrOn } from '@theme/dip-arm';
import { hhOn, split as hhSplit, paintBar as hhPaintBar, lineNote as hhLineNote, trialCtaWord, dueText as hhDueText } from '@theme/dip-hh';
import { paintPrice, bindChoice, scrollToStep, shipSaved } from '@theme/dip-evr';

/**
 * Read the basket's item count out of rendered section HTML.
 * Duplicated byte-identically in dip-buy-box.js, dip-home.js, dip-cart.js and here;
 * keep the copies in sync.
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
// Shopify stores every price as amount x 100 regardless of currency - see dip-buy-box.js.
const MINOR = 100;
const money = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);
const _fmt0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyTidy = (minorUnits) => ((minorUnits || 0) % MINOR === 0 ? _fmt0 : _fmt).format((minorUnits || 0) / MINOR);

// Plan maths (WPM / MIN_WEEKS / WEEK_WORDING_MAX / MONTH_CAP / the snap) lives in
// assets/dip-plan-engine.js: one implementation, shared with dip-routine.js and the grid harness.

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
/** The plan thumbnail for a product at a scent: the per-scent map first (variant image or per-scent setting), else the product's thumb. */
const thumbFor = (p, scent) => (p?.thumbs && scent && p.thumbs[scent]) || p?.thumb || '';

// Inline icons (match dip-buy-box.js)
const I = {
  checkMini: '<svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6l3 3 5-6" stroke="currentColor" stroke-width="2" fill="none"/></svg>',
  // The canonical .dip-box tick (the bundle pick cards / the .dip-addon row).
  boxTick: '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.2" aria-hidden="true"><path d="M4.5 12.5 10 18 19.5 6.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  // The add-on row's scent mark (the Rich module's .dip-addon-scent icon, verbatim).
  scent: '<svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true"><g stroke="currentColor" stroke-width="1"><circle cx="7" cy="7" r="1.6"/><ellipse cx="7" cy="3" rx="1.7" ry="2.3"/><ellipse cx="7" cy="11" rx="1.7" ry="2.3"/><ellipse cx="3" cy="7" rx="2.3" ry="1.7"/><ellipse cx="11" cy="7" rx="2.3" ry="1.7"/></g></svg>',
};
// The stroked button arrow (snippets/dip-btn-arrow.liquid is the source; keep the path in step).
// The plan step's semantic id (1 scent / 2 usage / 3 load / 4 conditioner question / 5 plan).
const PLAN = 5;
const BTN_ARROW = '<svg class="dip-btn__arrow" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 10h13M11 4.5l5.5 5.5-5.5 5.5"/></svg>';

class DipSimpleBuy extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dip-data]');
    if (!dataEl) return;
    try {
      this.model = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[dip-simple-buy] bad data island', e);
      return;
    }

    this.labels = this.model.labels || {};
    // The sitewide first-order offer's display state (snippets/dip-offer.liquid). `active` is
    // already false for a logged-in past purchaser; a missing object means no offer.
    this.offer = this.model.offer || { active: false };
    // The multi-surface hardware (snippets/dip-msc-hardware.liquid): bottle + cloth variants,
    // per-size quantities, zeroing codes, worth figures.
    this.hardware = this.model.hardware || null;
    this.main = this.model.main || { key: '', isMsc: false };

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      // The Everything path's embedded routine chooser owns its refs (cta, once, error...).
      if (el.closest('dip-routine')) return;
      const k = el.getAttribute('ref');
      if (!(k in this.refs)) this.refs[k] = el;
    });
    this.form = this.refs.form;

    // The Everything split test. Arm B opens on the choice step (semantic id 0) with the embedded
    // routine chooser waiting behind it; arm A (and the test off) removes both, so today's steps
    // run exactly as they did.
    this.evrRoutine = this.querySelector('dip-routine[data-embed]');
    this.evr = !!(this.refs.evrStep && this.evrRoutine && evrOn());
    if (!this.evr) {
      this.refs.evrStep?.remove();
      this.evrRoutine?.remove();
      delete this.refs.evrStep;
      this.evrRoutine = null;
    }
    this.choice = null;      // 'one' | 'all' once the choice step is confirmed
    this.choicePick = null;  // the card currently ticked

    this.scentColours = this.model.scentColours || {};
    this.scentRadios = [...this.querySelectorAll('input[name="dip-scent"]')];
    this.loadRadios = [...this.querySelectorAll('input[name="dip-load"]')];

    // Chooser state
    this.scent = this.scentRadios.find((r) => r.checked)?.value || this.model.scents?.[0] || '';
    this.washes = Number(this.dataset.defaultWashes) || 4;
    this.max = Number(this.dataset.washesMax) || 14;
    // No load radios rendered (show_load_step off) = the authored fixed factor stands in
    // (dishwasher runs: 1; MSC bottles-a-month: 0.0577 = packs/week per bottle-a-month).
    this.factor = Number(this.loadRadios.find((r) => r.checked)?.value)
      || (this.loadRadios.length ? 1.5 : Number(this.dataset.fixedFactor))
      || 1;
    // Per-unit price divisor multiplier (MSC: a pack of 4 sheets = 4 bottles).
    this.unitMul = Number(this.dataset.unitPerWash) || 1;
    // Stepped flow: one question at a time; completed steps collapse to an answer row and
    // stay tappable (easy back-navigation); the plan is the final reveal. Steps are whichever
    // of the semantic ids (1 scent / 2 usage / 3 load / 4 plan) the section actually rendered.
    this.steps = this.#ownSteps().map((el) => Number(el.dataset.step));
    // Not `|| 1`: the Everything test's choice step is semantic step 0.
    this.activeStep = this.steps.length ? this.steps[0] : 1;
    this.reached = this.activeStep;   // furthest step unlocked
    this.revealed = false;

    // The tier block (ruling 16). tiers[0] is always Basic (this product alone); the others
    // name companion product keys. Basic pre-selected, so the advertised entry price is what
    // an undecided shopper carries to checkout. The "Fewer deliveries" card is a synthetic tier
    // right after Basic when the page turns it on (round 2).
    this.tiers = (this.model.tiers || []).filter(Boolean);
    if (this.model.bumpCard && this.tiers.length) {
      this.tiers = [this.tiers[0], { key: 'bump', label: this.model.bumpLabel || 'Fewer deliveries', bump: true, products: [] }, ...this.tiers.slice(1)];
    }
    this.tier = 0;
    // Companion lines a shopper has unticked (ruling 15: every line removable). Keyed by
    // product, so a choice survives switching tiers.
    this.off = {};
    // The "fewer deliveries" pack switch (ruling 10): the main product's bigger pack at the
    // same rate, carried through every tier.
    this.bumped = false;
    // Companion scent choices (laundry as a companion has three types).
    this.compScent = {};
    Object.entries(this.model.companions || {}).forEach(([k, p]) => { this.compScent[k] = p?.scents?.[0] || ''; });
    // The conditioner question (round 2): "Do you use fabric conditioner? Yes / No" as its own
    // step; a yes puts the companion line into every plan card, matched to the main pack and
    // cadence. Nothing pre-selected; the step confirms with Next once answered.
    this.addon = (this.model.addon && this.model.companions?.[this.model.addon.key]) ? this.model.addon : null;
    this.addonRadios = [...this.querySelectorAll('input[name="dip-addon"]')];
    this.addonAns = this.addonRadios.find((r) => r.checked)?.value || null;
    this.onceAdded = false;

    // Pack ladder: distinct wash counts, ascending, from the variants actually on the product,
    // limited to the sizes the section offers (only_configured_sizes hides the stock-up sizes
    // from this PDP; the ladder must not resurrect them: ruling 10).
    this.packs = packsOf(this.model);

    this.#bind();
    if (this.evr) this.#initEverything();
    this.#renderSteps();
    this.render();
    this.#initGallery();
    this.#initCarousel();
    this.#initTabsets();
    this.#initHowLink();
    this.#initTpPop();
    this.#initSticky();
    this.#publishGuardConfig();
  }

  // ---- Stepped flow ------------------------------------------------------

  #advance(n) {
    this.reached = Math.max(this.reached, n);
    this.#setActive(n);
    if (n >= PLAN) this.#reveal();
  }

  // The next step actually rendered after semantic step n (the scent and load steps are
  // optional, so "n + 1" is not guaranteed to exist).
  #stepAfter(n) {
    const later = this.steps.filter((x) => x > n);
    return later.length ? later[0] : PLAN;
  }

  #setActive(n) {
    if (n > this.reached) return;
    this.activeStep = n;
    this.#renderSteps();
    // Keep the newly-opened step in view on small screens; instant, reduced-motion safe.
    const head = this.refs[`stepHead${n}`] || (n === PLAN ? this.refs.step5 : null);
    // Arm B: the top of the opened step, not its Next button (Charlie 2026-09-30).
    if (this.evr) scrollToStep(this.#ownSteps().find((el) => Number(el.dataset.step) === n));
    else if (head?.scrollIntoView) head.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    window.dispatchEvent(new Event('resize'));
  }

  #reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.classList.add('is-revealed');
    this.render();
    // The sticky module measures its reveal line from layout; the plan step just expanded, so
    // let it re-measure (it listens to resize).
    window.dispatchEvent(new Event('resize'));
  }

  // This chooser's own steps (never the embedded routine's).
  #ownSteps() {
    return [...this.querySelectorAll('.dip-sstep')].filter((el) => !el.closest('dip-routine'));
  }

  #renderSteps() {
    const L = this.labels;
    this.#ownSteps().forEach((el) => {
      const n = Number(el.dataset.step);
      const active = n === this.activeStep || (n === PLAN && this.revealed);
      const done = n < PLAN && this.reached > n;
      el.classList.toggle('is-active', active);
      el.classList.toggle('is-done', done && !active);
      el.classList.toggle('is-locked', n > this.reached && !(n === PLAN && this.revealed));
      const head = el.querySelector('.dip-sstep__head');
      if (head?.tagName === 'BUTTON') {
        head.setAttribute('aria-expanded', active ? 'true' : 'false');
        head.disabled = n > this.reached;
      }
    });
    if (this.refs.ans1) this.refs.ans1.textContent = this.scent;
    if (this.refs.ans2) {
      this.refs.ans2.textContent = (L.ansWashes || '[n] washes a week')
        .replace('[n]', this.washes >= this.max ? `${this.max}+` : String(this.washes));
    }
    if (this.refs.ans3) {
      const r = this.loadRadios.find((x) => x.checked);
      this.refs.ans3.textContent = r?.dataset.title || '';
    }
    if (this.refs.ans4 && this.addon) {
      this.refs.ans4.textContent = this.addonAns === 'yes' ? (this.addon.yesAns || 'Yes') : this.addonAns === 'no' ? (this.addon.noAns || 'No') : '';
    }
  }

  // ---- The Everything test ------------------------------------------------

  // Arm B: price both choice cards, bind the step, number every step after it, and mirror the
  // embedded routine into the floating bar once Everything is chosen.
  #initEverything() {
    this.classList.add('is-evr');
    const step = this.refs.evrStep;
    // Steps after the choice step read 2, 3, ... (the Liquid numbers them for arm A), and none of
    // them shows until the path is chosen (Charlie 2026-09-30: they only make sense after it).
    this.#ownSteps().filter((el) => el !== step).forEach((el, i) => {
      const n = el.querySelector('.dip-sstep__n'); if (n) n.textContent = String(i + 2);
      el.hidden = true;
    });
    this.evrRoutine.dataset.stepOffset = '1';
    bindChoice(step, (v) => { this.choicePick = v; this.#track('evr', { choice: v }); });
    this.refs.evrNext?.addEventListener('click', () => this.#confirmChoice());
    // Reopening the choice hides both paths until it is confirmed again (round 3: nothing from the
    // other path may linger), and the floating bar goes back to its pick state meanwhile.
    this.refs.evrHead?.addEventListener('click', () => {
      if (this.activeStep === 0) return;
      this.choosing = true;
      this.#ownSteps().filter((el) => el !== step).forEach((el) => { el.hidden = true; });
      if (this.evrRoutine) this.evrRoutine.hidden = true;
      this.choice = null;
      this.classList.remove('is-evr-all');
      this.#setActive(0);
      this.render();
    });
    this.evrRoutine.addEventListener('dip:routine-state', (e) => this.#mirrorSticky(e.detail));
    // The routine chooser is its own module: price the Everything card once it has booted.
    customElements.whenDefined('dip-routine').then(() => this.#paintChoice());
    this.classList.add('is-evr-ready');
  }

  // The one-product card at this product's smallest pack (its honest "from"), and the Everything
  // card from the embedded routine (every product at its smallest pack).
  #paintChoice() {
    const step = this.refs.evrStep;
    if (!step) return;
    const card = this.#card(this.packs[0]);
    const one = step.querySelector('[data-evr-card="one"]');
    const E = this.evrRoutine?.model?.evr || {};
    if (card && one) {
      // The same whole-saving rule as the Everything banner: the offer, free first delivery where
      // the order would otherwise pay it, and (multi-surface) the bottle and cloth at their worth.
      const hw = this.main.isMsc && hwActive(this.hardware) ? (Number(hwWorth(this.hardware, card.variant.size)) || 0) : 0;
      const save = (card.price.on ? card.price.saving : 0)
        + shipSaved(card.price.first, card.price.on, this.offer, Number(this.model.freeShipMinor) || 0) + hw;
      paintPrice(one, { first: card.price.first, raw: card.price.raw, on: card.price.on, save }, {
        from: E.from ?? 'From', offer: this.offer, money, saveTpl: E.singleSave || 'Save [save]',
      });
    }
    this.evrRoutine?.paintEverything?.(step);
  }

  #confirmChoice() {
    const v = this.choicePick;
    if (!v) return;
    // A different path from the last one confirmed starts fresh (round 3, Charlie 2026-09-30).
    const changed = this.choiceConfirmed != null && this.choiceConfirmed !== v;
    this.choiceConfirmed = v;
    this.choosing = false;
    this.choice = v;
    const picked = this.refs.evrStep?.querySelector('input[name="dip-evr"]:checked');
    if (this.refs.evrAns) this.refs.evrAns.textContent = picked?.dataset.title || '';
    const own = this.#ownSteps().filter((el) => el !== this.refs.evrStep);
    this.classList.toggle('is-evr-all', v === 'all');
    if (v === 'all') {
      // Everything: this product's own questions step aside, the routine's take over.
      own.forEach((el) => { el.hidden = true; });
      if (changed) this.evrRoutine.reset?.();
      this.evrRoutine.hidden = false;
      this.reached = Math.max(this.reached, 1);
      this.activeStep = -1;
      this.#renderSteps();
      this.evrRoutine.start();
      this.#track('evr_confirm', { choice: v });
      return;
    }
    if (changed) {
      // This product's own questions from the top: nothing done, no plan revealed.
      this.reached = 0;
      this.revealed = false;
      this.classList.remove('is-revealed');
    }
    own.forEach((el) => { el.hidden = false; });
    this.evrRoutine.hidden = true;
    this.#track('evr_confirm', { choice: v });
    // The routine's scarcity products no longer apply: this product's plan re-announces.
    this.render();
    this.#advance(this.#stepAfter(0));
  }

  // Everything chosen: the floating bar speaks for the routine (its first-order total and roll-on).
  #mirrorSticky(d) {
    if (this.choice !== 'all' || !d) return;
    this.revealedAll = !!d.revealed;
    if (this.refs.stickybar) this.refs.stickybar.classList.toggle('dip-stickybar--pick', !d.revealed);
    if (this.refs.sbCta) {
      if (d.revealed) {
        this.refs.sbCta.className = 'dip-sb-cta';
        this.refs.sbCta.textContent = this.labels.ctaWord || 'Add to basket';
      } else {
        this.refs.sbCta.className = 'dip-btn dip-btn--lg dip-btn--offer dip-sb-cta-pick';
        this.refs.sbCta.innerHTML = `<span class="dip-btn__label"></span>${BTN_ARROW}`;
        this.refs.sbCta.querySelector('.dip-btn__label').textContent = this.refs.evrStep?.dataset.sticky || this.evrRoutine?.E?.sticky || 'Start your routine';
      }
    }
    if (this.refs.sbPrice) this.refs.sbPrice.textContent = d.offerOn ? `${money(d.first)} ${d.label}` : money(d.first);
    if (this.refs.sbBilled) this.refs.sbBilled.textContent = d.billed || '';
    if (this.refs.sbSave) {
      const diff = d.offerOn ? d.renew - d.first : 0;
      this.refs.sbSave.hidden = !(diff > 0);
      if (diff > 0) this.refs.sbSave.textContent = `${this.labels.saveWord || 'Save'} ${money(diff)}`;
    }
    window.dispatchEvent(new Event('resize'));
  }

  // ---- Plan maths -------------------------------------------------------

  // washes-per-week drawn off the pack (a big load = a whole sheet = two washes' worth)
  get rate() { return this.washes * this.factor; }

  // One computed card for THIS product: variant + real plan + derived figures. The maths, its
  // constants and the honest month-snap live in assets/dip-plan-engine.js so this chooser,
  // the routine page and the grid harness can never disagree on the plan for the same answers.
  #card(pack) {
    const variant = variantFor(this.model.variants, pack, this.scent);
    if (!variant) return null;
    const ideal = cadenceFor(pack, this.rate);
    const plan = planFor(variant, ideal);
    if (!plan) return null;
    // Per WEEK, not per month: the chooser is week-denominated end to end, and a month is
    // ~4.33 weeks, so a per-month equivalent EXCEEDS the per-delivery charge on 4-week plans
    // (12.00 billed rendering as "13.00/mo" read as an error). Weekly is always smaller than
    // the bill, so it is unmistakably a reference figure (Charlie 2026-08-26, plan doc s32).
    const perWeek = plan.raw / plan.cad.weeks;
    const label = cadenceLabel(plan.cad);
    return {
      pack, variant, plan, perWeek, label,
      // Per UNIT, not per box-count: unitMul is 1 everywhere the box counts the unit itself,
      // and 4 on MSC (a pack of 4 sheets = 4 bottles of cleaner). Always the roll-on price:
      // the per-unit figure is the for-life comparison between packs, not a first-order one.
      perWash: plan.raw / (pack * this.unitMul),
      // The first-order offer on this line: { raw, first, saving, on, rollOn }. `on` is false
      // for an ineligible size or a shopper the discount would refuse, and then `first` is
      // simply the plan price and `rollOn` the plain "billed ... + delivery" form.
      price: priceLine(plan.raw, label, this.offer, variant, money),
    };
  }

  // Product-noun template fill: [n] = box-counted washes, [size] = the size value verbatim,
  // [cad] = the booked cadence.
  #sizeText(tmpl, card) {
    return String(tmpl || '')
      .replace('[n]', String(card.pack))
      .replace('[size]', card.variant.size || '')
      .replace('[cad]', card.label);
  }

  /** The main product's line as the chosen card sees it (the matched pack, or the bigger pack
   *  on the "Fewer deliveries" card). */
  get mainCard() { return this.tierData?.[this.tier]?.lines?.[0]?.card || this.base; }

  // ---- Companion lines (the tiers' other products) -----------------------

  /**
   * One companion product's line. Fabric conditioner on the laundry page MIRRORS the laundry
   * pack and rides its cadence (ruling 16; the engine snaps to a month where fabcon has no
   * weekly plan yet). Every other companion books its authored companion size at the cadence
   * the engine gives its default usage answer, which is the answer that product's own page
   * pre-selects, so both surfaces agree (the grid asserts it). Null when unresolvable.
   */
  #compLine(key, main) {
    const p = this.model.companions?.[key];
    if (!p?.variants?.length) return null;
    const hw = this.hardware;
    const scent = this.compScent[key] ?? (p.scents?.[0] || '');
    main = main || this.base;
    // The booking rule lives in the engine (companionBooking), shared with the grid harness.
    const booked = companionBooking(p, {
      scent, mainPack: main?.pack || 0, mainCad: main?.plan?.cad || null, mainRate: this.rate,
    });
    if (!booked) return null;
    const { variant, cad } = booked;
    const plan = planFor(variant, cad);
    if (!plan) return null;
    const label = cadenceLabel(plan.cad);
    const price = priceLine(plan.raw, label, this.offer, variant, money);
    const hwOn = p.isMsc && hwActive(hw);
    return {
      key, p, variant, plan, label, price, scent,
      scents: (p.scents || []).filter(Boolean),
      pack: Number(variant.washes) || 0,
      hw: hwOn ? { lines: hwLines(hw, variant.size), text: hwIncludedText(hw, variant.size), worth: hwWorth(hw, variant.size) } : null,
    };
  }

  /** One tier, fully computed: its lines (main first), which are on, and its totals. */
  #tier(t, idx) {
    // The "Fewer deliveries" card is the bigger pack; every other card is the matched pack.
    const main = t.bump ? this.bump : this.base;
    if (!main) return null;
    const hwOn = this.main.isMsc && hwActive(this.hardware);
    const lines = [{
      key: this.main.key, main: true, card: main, variant: main.variant, plan: main.plan,
      label: main.label, price: main.price, pack: main.pack, on: true,
      hw: hwOn ? { lines: hwLines(this.hardware, main.variant.size), text: hwIncludedText(this.hardware, main.variant.size), worth: hwWorth(this.hardware, main.variant.size) } : null,
    }];
    let resolved = 0;
    (t.products || []).forEach((key) => {
      if (key === this.main.key) return;
      const l = this.#compLine(key, main);
      if (!l) return;
      resolved += 1;
      lines.push({ ...l, on: !this.off[key] });
    });
    // A companion tier none of whose products resolved is Basic in disguise: hide it.
    if (idx > 0 && (t.products || []).length && !resolved) return null;
    // The conditioner question answered yes: its line joins every card (fixed, no tick: the
    // question is where it is added or removed), unless the card already carries that product.
    if (this.addon && this.addonAns === 'yes' && !lines.some((l) => l.key === this.addon.key)) {
      const l = this.#compLine(this.addon.key, main);
      if (l) lines.push({ ...l, on: true, addon: true });
    }
    const on = lines.filter((l) => l.on);
    const firstRaw = on.reduce((s, l) => s + l.price.first, 0);
    const subRaw = on.reduce((s, l) => s + l.price.raw, 0);
    const otRaw = on.reduce((s, l) => s + (Number(l.variant.otRaw) || 0), 0);
    return {
      t, idx, lines, on, firstRaw, subRaw, otRaw,
      saving: subRaw - firstRaw, offerOn: firstRaw < subRaw,
      // "Single" = no companion tier; a question line still renders inside the single card.
      single: lines.filter((l) => !l.addon).length === 1,
      withAddon: lines.some((l) => l.addon),
    };
  }

  // ---- Rendering --------------------------------------------------------

  render() {
    const L = this.labels;

    // Scent re-theming, same contract as dip-buy-box.js: --sel is the light tint (the selected
    // plan's background), --sel-ink the legible companion (active borders, selection ticks).
    const sc = this.scentColours[this.scent];
    if (sc) {
      this.style.setProperty('--sel', sc.sel || '#F4F7FB');
      this.style.setProperty('--sel-ink', sc.selInk || 'var(--dip-accent-ink)');
    }
    this.scentRadios.forEach((r) => {
      const label = r.closest('.dip-scent-pill');
      if (!label) return;
      const col = this.scentColours[r.value] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
      const on = r.checked;
      label.classList.toggle('is-selected', on);
      const sw = label.querySelector('.dip-scent-sw');
      if (sw) sw.innerHTML = on ? I.checkMini : '';
    });

    this.base = this.#card(basePack(this.packs, this.rate));
    if (!this.base) return;
    // The bump: the next pack up at the same rate, only while its cadence stays sane, and only
    // while it actually changes the bill.
    const bp = bumpPack(this.packs, this.base.pack, this.rate);
    this.bump = bp ? this.#card(bp) : null;
    if (this.bump && this.bump.plan.raw === this.base.plan.raw) this.bump = null;

    // The tier block first: the chosen card decides the main pack everything else quotes.
    this.tierData = this.tiers.map((t, i) => this.#tier(t, i));
    if (!this.tierData[this.tier]) this.tier = 0;
    this.tierData.forEach((td, i) => this.#renderTier(this.refs[`tier${i}`], td, i === this.tier));
    this.#renderSummary(this.chosenTier);
    for (let i = this.tierData.length; this.refs[`tier${i}`]; i += 1) this.refs[`tier${i}`].hidden = true;
    this.bumped = !!this.tierData[this.tier]?.t?.bump;
    const c = this.mainCard;
    this.#renderAddonPrice();

    // Live sentence (the reveal's headline)
    if (this.refs.live) {
      this.refs.live.textContent = this.#sizeText(L.liveLine || 'A [n]-wash pack, delivered [cad].', c);
    }
    if (this.refs.liveSub) {
      this.refs.liveSub.textContent = (L.liveSub || 'Based on [n] washes a week. Carbon neutral delivery just before you run out.')
        .replace('[n]', String(this.washes >= this.max ? `${this.max}+` : this.washes));
    }
    this.#renderBumpLink();

    this.variant = c.variant;
    if (this.refs.variantId) this.refs.variantId.value = c.variant.id;
    // The scarcity box counts the products in the chosen card (Charlie 2026-09-07): announce them.
    this.#announcePlaces((this.chosenTier?.on || []).map((l) => l.key).filter(Boolean));

    this.#renderHardware(c);
    this.#renderCtas();
  }

  // The "fewer deliveries" switch (ruling 10: heavy households see the bigger pack beside the
  // matched one, defaulting small). Text from the authored templates; every figure computed.
  #renderBumpLink() {
    const el = this.refs.bumpLink;
    if (!el) return;
    const L = this.labels;
    // D6 (Charlie 2026-09-05): the switch is off unless the section setting turns it on; the tiers
    // carry the upsell and the cadence upgrade lives post-purchase.
    if (!L.bumpSwitch || this.model.bumpCard) { el.hidden = true; return; }
    el.hidden = !this.bump;
    if (!this.bump) return;
    const alt = this.bumped ? this.base : this.bump;
    const tpl = this.bumped
      ? (L.bumpBack || 'Back to [n] washes [cad]')
      : (L.bumpLink || 'Fewer deliveries: [n] washes [cad], [first], [pct]% less per wash');
    const pct = this.base.perWash > 0 ? Math.round((1 - this.bump.perWash / this.base.perWash) * 100) : 0;
    let txt = tpl;
    if (pct <= 0) txt = txt.replace(/,?\s*[^,]*\[pct\][^,]*/, '');
    txt = txt
      .replace('[n]', String(alt.pack))
      .replace('[size]', alt.variant.size || '')
      .replace('[cad]', alt.label)
      .replace('[first]', alt.price.on ? `${money(alt.price.first)} ${firstLabel(this.offer)}` : money(alt.plan.raw))
      .replace('[pct]', String(pct));
    const tx = el.querySelector('[data-tx]') || el;
    tx.textContent = txt;
    el.setAttribute('aria-pressed', this.bumped ? 'true' : 'false');
  }

  // The Yes option's price tail: the conditioner's first-order price and roll-on line at the
  // matched pack (computed; the question never states a price it cannot honour).
  #renderAddonPrice() {
    const el = this.querySelector('[data-addon-price]');
    if (!el || !this.addon) return;
    const l = this.#compLine(this.addon.key, this.base);
    if (!l) { el.textContent = ''; return; }
    // Half now, half later, arm B: what the add-on adds TODAY ("Adds £4.50 today, then £9.00 every 5 weeks.").
    const tpl = (hhOn() && this.offer?.hh?.addonYes) || this.addon.yesPrice || '[first] first order, then [price] [cad]';
    el.textContent = l.price.on
      ? tpl.replace('[first]', money(l.price.first)).replace('[price]', money(l.price.raw)).replace('[cad]', l.label)
      : `${money(l.price.raw)} ${l.label}`;
    // The offer's saving on the conditioner as the pink chip beside the Yes title (round 3).
    const save = this.querySelector('[data-addon-save]');
    if (save) save.textContent = l.price.on && l.price.saving > 0 ? (this.labels.tierSave || 'Save [amount]').replace('[amount]', money(l.price.saving)) : '';
  }

  // Thumbnails of every product in a card (the theme's representative images, one source for
  // every surface): the main product first, then each companion in the card (round 3: "small
  // thumbnails, not the variant images", in place of the ticked breakdown).
  #renderThumbs(el, td) {
    const box = el.querySelector('[data-thumbs]');
    if (!box) return;
    // Charlie 2026-09-08: the thumbnail follows the chosen type (per-scent map, else the product's thumb).
    const srcs = td.lines.filter((l) => l.on).map((l) => thumbFor(l.main ? this.main : l.p, l.variant?.scent)).filter(Boolean);
    box.innerHTML = srcs.map((src) => `<span class="dip-kit-ic dip-splan__thumb"><img class="dip-kit-photo" src="${esc(src)}" alt="" loading="lazy" width="88" height="88"></span>`).join('');
    box.hidden = !srcs.length;
  }

  // The per-unit line, with the fewer-deliveries card's percentage saving against the matched
  // pack beside it (round 3: "present the per wash percentage saving and price too").
  #perwashHtml(card, t) {
    const L = this.labels;
    let html = esc((L.perwash || '[price] per wash').replace('[price]', money(card.perWash)));
    // Round 13: the "% less per wash" clause moved into the card's benefit bullets.
    return html;
  }

  // The card's roll-on line when it carries several products (round 3: no per-line breakdown):
  // every line's renewal price and cadence, delivery once when every parcel charges the same.
  //   then £12.00 every 5 weeks and £9.00 every month + £3.95 delivery
  #rollOnCompact(td) {
    const L = this.offer?.labels || {};
    const on = td.on;
    const lead = td.offerOn ? 'then ' : '';
    // Lines on the same cadence blend into one figure ("then £21.00 every 5 weeks") AND ship as
    // one parcel, so delivery is judged on the cadence group's sum (Recharge merges same-day
    // charges into one order; the free-delivery threshold applies to that order). Fixed
    // 2026-09-08: judging per line showed "+ £3.95 delivery" on a £63.00 parcel.
    const byCad = new Map();
    on.forEach((l) => byCad.set(l.label, (byCad.get(l.label) || 0) + l.price.raw));
    const groups = [...byCad].map(([label, raw]) => ({ label, raw, ship: renewalShipMinor(raw, this.offer) }));
    const shipTxt = (s) => (s ? money(s) : (L.free || 'free'));
    const sameShip = groups.every((g) => g.ship === groups[0].ship);
    if (!sameShip) {
      // Mixed: each parcel states its own delivery ("£36.00 every 8 weeks + free delivery and
      // £12.00 every 6 weeks + £3.95 delivery").
      return lead + groups.map((g) => `${money(g.raw)} ${g.label} + ${shipTxt(g.ship)} delivery`).join(' and ');
    }
    return `${lead}${groups.map((g) => `${money(g.raw)} ${g.label}`).join(' and ')} + ${shipTxt(groups[0].ship)} delivery`;
  }

  /** The chosen card's roll-on clause: one line, the cadence-blended compact form, or the pointer to the rows. */
  #rollOnFor(td) {
    const L = this.labels;
    if (!td || !td.on.length) return '';
    if (td.on.length === 1) return td.on[0].price.rollOn;
    const cads = new Set(td.on.map((l) => l.label));
    return cads.size === 1 ? this.#rollOnCompact(td) : (L.stickyMulti || 'then each product at its own price, listed above');
  }

  /**
   * The order summary under the plan cards (Charlie 2026-09-08, round 12): the routine page's canonical
   * summary card, fed by the CHOSEN card. One row per line (thumbnail following the scent, name, size ·
   * scent · cadence, the first-order figure over its roll-on line), the multi-surface hardware row tagged
   * FREE, the savings stack (the offer as a subtraction, free first delivery only when it saves money, the
   * total), then the price block (struck plan total, first-order total, the roll-on clause). Roll-on lines
   * here are judged per cadence group (one parcel), computed locally so shared line objects stay untouched.
   */
  #renderSummary(td) {
    // Round 14 (Charlie 2026-09-08): the "In your plan" card is gone; what remains under the cards is the
    // "Your plan" price line: struck plan total, the first-order figure and its label, the roll-on clause inline.
    // Round 15 (Charlie 2026-09-08): no struck total here (the cards already show it); the line reads as ONE
    // sentence, "£6.00 first order, then £12.00 every 5 weeks + £3.95 delivery", wrapping like prose.
    const R = this.refs;
    if (!td) return;
    if (R.sumHeadline) R.sumHeadline.innerHTML = td.offerOn ? `${money(td.firstRaw)} <small>${esc(firstLabel(this.offer))}</small>` : money(td.subRaw);
    const roll = this.#rollOnFor(td);
    if (R.sumBilled) R.sumBilled.textContent = roll ? (td.offerOn ? `, ${roll}` : ` ${roll}`) : '';
    // Half now, half later, arm B: the 14-day bar (today, the rest + delivery due on the date).
    hhPaintBar(R.hhBar, hhSplit(td.on.map((l) => l.price), this.offer), this.offer, money, roll);
  }

  /**
   * One tier card. Basic renders exactly as the signed matched-plan card (size line, roll-on
   * line, struck plan price over the first-order figure, per-unit price). A companion tier
   * shows the tier's total in the price column and itemises every line beneath (ruling 15).
   */
  #renderTier(el, td, selected) {
    if (!el) return;
    el.hidden = !td;
    if (!td) return;
    const L = this.labels;
    const t = td.t;
    el.classList.toggle('is-selected', selected);
    // Charlie 2026-09-08 (round 15): on a page whose cards are all THIS product (laundry, multi-surface:
    // the matched pack and the fewer-deliveries pack, either with the conditioner from the question) the
    // head keeps its title beside the thumbnail on phones and stacks the price under it. A page with a
    // companion card (dishwasher, fabric conditioner: "With laundry sheets", a longer authored title)
    // keeps the wrapped head on every card, so its cards match each other. CSS: .dip-splan--compact.
    el.classList.toggle('dip-splan--compact', !(this.tiers || []).some((x) => x.products && x.products.length));
    const input = el.querySelector('input[name="dip-plan"]'); if (input) input.checked = selected;
    const set = (sel, v) => { const n = el.querySelector(sel); if (n) n.textContent = v; };
    const main = td.lines[0];
    set('[data-role]', t.label || '');
    const banner = el.querySelector('[data-banner]');
    if (banner) { banner.textContent = t.banner || ''; banner.hidden = !t.banner; }
    // Thumbnails of what is in the card (round 3), where the ticked breakdown used to be.
    this.#renderThumbs(el, td);
    // Every card above the first is promoted, not silent (rounds 4 to 9): the pink banner leads
    // with what the step up costs on the first order, bold ("Upgrade for only £4.50"), then the
    // card's benefit: the fewer-deliveries card's computed clauses (half the deliveries, lower
    // delivery CO2, the per-unit saving), or a companion card's authored line. All computed.
    if (banner) {
      const first = this.tierData?.[0];
      const diff = first && td.offerOn && td.idx > 0 ? td.firstRaw - first.firstRaw : 0;
      // Half now, half later, arm B: the step up is stated as what it adds today ("Upgrade for £4.50 more today"),
      // never "only" (the same again is due with the rest), and a companion card's saving line is not shown.
      const hh = hhOn();
      const lead = diff > 0
        ? (hh ? (this.offer?.hh?.upgradeLead || 'Upgrade for [diff] more today') : (L.upgradeLead || 'Upgrade for only [diff]')).replace('[diff]', money(diff))
        : '';
      // Charlie 2026-09-08 (round 12): the pink bar says ONE thing. Fewer-deliveries card = the upgrade lead
      // alone ("UPGRADE FOR ONLY £4.50"); a companion card = its authored line alone ("Save 50% on laundry
      // sheets"), set in the same bold uppercase. The card's role label always shows.
      let text = '';
      if (t.bump) text = lead;
      else if (td.idx > 0 && !hh) text = t.banner || '';
      banner.innerHTML = text ? `<b>${esc(text)}</b>` : '';
      banner.hidden = !text;
    }
    // The fewer-deliveries card's benefits as icon bullets (round 13): half the deliveries (or n a
    // year, not m), lower delivery CO2, the per-wash saving. Icons come from the section's
    // <template data-benefit-icons> (snippets/dip-fact-icon.liquid), the reasons list's marks.
    const benefits = el.querySelector('[data-benefits]');
    if (benefits) {
      if (t.bump && this.base) {
        const perYear = (c) => Math.round(52 / (c?.plan?.cad?.weeks || 52));
        const nBump = perYear(main.card), nBase = perYear(this.base);
        const pct = this.base.perWash > 0 ? Math.round((1 - main.card.perWash / this.base.perWash) * 100) : 0;
        const half = nBase > 0 && nBump > 0 && Math.abs(nBase / nBump - 2) < 0.15;
        const items = [];
        if (nBump < nBase) items.push(['deliveries', half ? (L.bumpHalf || 'Half the deliveries') : (L.bumpFewer || '[n] deliveries a year, not [m]').replace('[n]', String(nBump)).replace('[m]', String(nBase))]);
        items.push(['co2', L.bumpCo2 || 'Lower delivery CO2']);
        if (pct > 0) items.push(['perwash', (L.bumpLess || '[pct]% less per wash').replace('[pct]', String(pct))]);
        const icons = this.querySelector('template[data-benefit-icons]')?.content;
        // The deliveries mark is a half symbol (Charlie 2026-09-08), the others the reasons list's icons.
        const icon = (k) => (k === 'deliveries' ? '<span class="dip-fact-half" aria-hidden="true">\u00bd</span>' : (icons?.querySelector(`[data-icon="${k}"]`)?.innerHTML || ''));
        benefits.innerHTML = items.map(([k, txt]) => `<li class="dip-fact">${icon(k)}<span>${esc(txt)}</span></li>`).join('');
        benefits.hidden = !items.length;
      } else {
        benefits.innerHTML = '';
        benefits.hidden = true;
      }
    }
    const perweek = el.querySelector('[data-perweek]');
    const was = el.querySelector('[data-was]');
    const lines = el.querySelector('[data-lines]');
    if (td.single && !td.withAddon) {
      const card = main.card;
      const pr = card.price;
      // The pound saving (ruling 13): the canonical pink chip beside the role.
      set('[data-save]', pr.on && pr.saving > 0 ? (L.tierSave || 'Save [amount]').replace('[amount]', money(pr.saving)) : '');
      set('[data-washes]', this.#sizeText(L.planSize || '[n] washes', card));
      if (pr.on) {
        // The offer display rule (offer decision rulings 2 + 13): the plan price struck, the
        // first-order figure as the headline with its label, and the itemised roll-on line
        // ("then £21.00 every 2 months + £3.95 delivery") in the billed slot. The struck
        // figure IS the genuine price of the same goods from order two.
        if (perweek) perweek.innerHTML = `${money(pr.first)} <small>${esc(firstLabel(this.offer))}</small>`;
        if (was) { was.hidden = false; was.textContent = money(pr.raw); }
      } else {
        if (perweek) perweek.innerHTML = `${money(card.perWeek)}<small>/week</small>`;
        // Struck was-price: the SAME pack bought one-time at the same cadence, per week (the
        // only genuine former price; the % chip never stands alone: IM8/Huel/AG1 pattern).
        if (was) {
          const otPw = (Number(card.variant.otRaw) || 0) / card.plan.cad.weeks;
          was.hidden = !(otPw > card.perWeek);
          was.textContent = otPw > card.perWeek ? `${money(otPw)}/week` : '';
        }
      }
      // Every subscription line states its renewal price, cadence AND delivery (ruling 4).
      set('[data-billed]', pr.rollOn);
      { const pw = el.querySelector('[data-perwash]'); if (pw) pw.innerHTML = this.#perwashHtml(card, t); }
      // No breakdown rows (round 3): the hardware strip above the chooser already states the
      // multi-surface bottle + cloth with their quantities.
      if (lines) { lines.innerHTML = ''; lines.hidden = true; }
      return;
    }
    // A companion tier (or a single card carrying the conditioner from the question): the
    // sentence (or "[n] washes, with fabric conditioner") in the title slot, the card TOTAL in the
    // price column (struck plan total over the first-order total), and the itemised lines below.
    set('[data-save]', td.offerOn ? (L.tierSave || 'Save [amount]').replace('[amount]', money(td.saving)) : '');
    set('[data-washes]', td.single ? this.#sizeText(this.addon?.withText || '[n] washes, with fabric conditioner', main.card) : (t.line || ''));
    if (td.offerOn) {
      if (perweek) perweek.innerHTML = `${money(td.firstRaw)} <small>${esc(firstLabel(this.offer))}</small>`;
      if (was) { was.hidden = false; was.textContent = money(td.subRaw); }
    } else {
      if (perweek) perweek.innerHTML = `${money(td.subRaw)} <small>${esc(L.todayWord || 'today')}</small>`;
      if (was) { was.hidden = true; was.textContent = ''; }
    }
    // The card-level roll-on line (every product's renewal, delivery once) and this product's
    // per-unit price; no ticked breakdown rows (round 3). Only a companion's scent picker stays
    // under the head, while this card is the chosen one.
    set('[data-billed]', this.#rollOnCompact(td));
    { const pw = el.querySelector('[data-perwash]'); if (pw) pw.innerHTML = this.#perwashHtml(main.card, t); }
    if (lines) {
      const scents = selected ? td.lines.filter((l) => !l.main && l.on && (l.scents || []).length > 1).map((l) => this.#scentsHtml(l)).join('') : '';
      lines.innerHTML = scents;
      lines.hidden = !scents;
      if (scents) this.#paintCompScents(lines);
    }
  }

  // A companion's scent pills (laundry as a companion has three types), one row.
  #scentsHtml(l) {
    const L = this.labels;
    return `<li class="dip-sline dip-sline--scents"><span class="dip-addon-scents" role="radiogroup" aria-label="${esc(L.compScent || 'Scent')}">`
      + l.scents.map((sc) => `<label class="dip-scent-pill dip-scent-pill--sm${sc === l.scent ? ' is-selected' : ''}" data-scent="${esc(sc)}">`
        + `<input type="radio" name="dip-comp-scent-${esc(l.key)}" value="${esc(sc)}"${sc === l.scent ? ' checked' : ''}>`
        + `<span class="dip-scent-sw" aria-hidden="true">${sc === l.scent ? I.checkMini : ''}</span>${esc(sc)}</label>`).join('')
      + '</span></li>';
  }

  #paintCompScents(scope) {
    scope.querySelectorAll('.dip-scent-pill[data-scent]').forEach((label) => {
      const col = this.scentColours[label.dataset.scent] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
    });
  }

  // Per-size hardware quantities: null unless this is the multi-surface page.
  #hwQty(card) {
    if (!(this.main.isMsc && hwActive(this.hardware))) return null;
    return hwQty(this.hardware, (card || this.mainCard)?.variant?.size);
  }

  // The hardware strip (MSC page): struck was-prices and the per-size quantity tags
  // ("2× Refillable spray bottle") move with the plan. Nothing renders off that page.
  #renderHardware(c) {
    const hq = this.#hwQty(c);
    if (!hq) return;
    const hw = this.hardware;
    const bottle = (Number(hw.bottleValue) || 0) * hq.bottle;
    const cloth = (Number(hw.clothValue) || 0) * hq.cloth;
    if (this.refs.bottleWas) this.refs.bottleWas.textContent = moneyTidy(bottle);
    if (this.refs.clothWas) this.refs.clothWas.textContent = moneyTidy(cloth);
    if (this.refs.bottleQtyTag) {
      this.refs.bottleQtyTag.hidden = !(hq.bottle > 1);
      if (hq.bottle > 1) this.refs.bottleQtyTag.textContent = `${hq.bottle}× `;
    }
    if (this.refs.clothQtyTag) {
      this.refs.clothQtyTag.hidden = !(hq.cloth > 1);
      if (hq.cloth > 1) this.refs.clothQtyTag.textContent = `${hq.cloth}× `;
    }
  }

  /** The chosen tier's computed data (falls back to Basic). */
  get chosenTier() { return this.tierData?.[this.tier] || this.tierData?.[0] || null; }

  #renderCtas() {
    const L = this.labels;
    const td = this.chosenTier;
    const c = this.mainCard;
    if (!td || !c) return;
    const ot = Number(c.variant.otRaw) || 0;
    // What the FIRST order charges: every line at its first-order price (the sitewide offer,
    // per eligible variant). Equals the plan total when no offer is in play, so every figure
    // below degrades to the plain plan price on its own.
    const firstRaw = td.firstRaw;
    const subRaw = td.subRaw;
    const offerOn = td.offerOn;
    const single = td.on.length === 1;
    // Several lines on ONE cadence blend into one roll-on line (the card's own grammar); only
    // mixed cadences fall back to the pointer at the itemised card.
    const cads = new Set(td.on.map((l) => l.label));
    const rollOn = this.#rollOnFor(td);
    if (this.refs.ctaLabel && !this.refs.ctaLabel.dataset.busy) {
      this.refs.ctaLabel.textContent = `${trialCtaWord(this.offer) || L.ctaWord || 'Add to basket'} · ${money(firstRaw)}`;
    }
    if (this.refs.once) {
      // The delivery a one-time order pays (the free-delivery threshold rule on its own price), so the one-time
      // figure is never read as like-for-like with the subscription's first payment (Charlie, 16 Oct 2026).
      const otShip = renewalShipMinor(ot, this.offer);
      this.refs.once.textContent = this.onceAdded ? '✓ Added to basket' : `One-Time Purchase · ${money(ot)}${otShip ? ` + ${money(otShip)} delivery` : ''}`;
    }
    // The one-time note is authored per page (the MSC page says the hardware still comes);
    // blank = hidden.
    if (this.refs.onceNote) this.refs.onceNote.hidden = !this.refs.onceNote.textContent.trim();
    // Everything chosen: the embedded routine drives the floating bar (#mirrorSticky).
    if (this.choice === 'all') return;
    // While the choice step is reopened (arm B), the bar is in its pick state whatever came before.
    const shown = this.revealed && !this.choosing;
    if (this.refs.stickybar) {
      this.refs.stickybar.classList.toggle('dip-stickybar--pick', !shown);
    }
    if (this.refs.sbCta) {
      if (shown) {
        this.refs.sbCta.className = 'dip-sb-cta';
        this.refs.sbCta.textContent = 'Add to basket';
      } else {
        // Pick mode wears the site's floating offer-button clothes (same classes as the
        // content pages' float), label + the canonical arrow.
        this.refs.sbCta.className = 'dip-btn dip-btn--lg dip-btn--offer dip-sb-cta-pick';
        this.refs.sbCta.innerHTML = `<span class="dip-btn__label"></span>${BTN_ARROW}`;
        this.refs.sbCta.querySelector('.dip-btn__label').textContent = (this.evr && (this.refs.evrStep?.dataset.sticky || this.evrRoutine?.E?.sticky)) || L.pickWord || 'Get Dip laundry sheets';
      }
    }
    // Floating bar: under the offer the headline is the first-order charge and the billed row
    // is the roll-on line (one line) or the pointer to the itemised lines (several products);
    // without it, the per-week reference (one line) or the plain total, and the billed line.
    if (this.refs.sbPrice) {
      if (offerOn) this.refs.sbPrice.textContent = `${money(firstRaw)} ${firstLabel(this.offer)}`;
      else if (single) this.refs.sbPrice.textContent = `${money(subRaw / c.plan.cad.weeks)}/week`;
      else this.refs.sbPrice.textContent = `${money(subRaw)} ${L.todayWord || 'today'}`;
    }
    // Half now, half later arm B (Charlie, 16 Oct): the second line is what is due on the date ("£9.50 + £3.95 delivery
    // due 30 Oct", the basket's wording) and the saving chip never shows (arm B makes no saving claim).
    const hhS = hhOn() && offerOn ? hhSplit(td.on.map((l) => l.price), this.offer) : null;
    const hhDue = hhS && hhS.rest > 0;
    if (this.refs.sbBilled) this.refs.sbBilled.textContent = hhDue ? hhDueText(hhS, this.offer, money) : rollOn;
    if (this.refs.sbSave && hhDue) this.refs.sbSave.hidden = true;
    else if (this.refs.sbSave) {
      // The pound saving the shopper actually sees (ruling 13): the offer's saving on the first
      // order when it applies, else the subscription saving against buying the same one-time.
      const diff = offerOn ? subRaw - firstRaw : td.otRaw - subRaw;
      this.refs.sbSave.hidden = !(diff > 0);
      if (diff > 0) this.refs.sbSave.textContent = `${L.saveWord || 'Save'} ${money(diff)}`;
    }
  }

  // ---- Events -----------------------------------------------------------

  #bind() {
    // Completed step headers reopen their step; answers are never wiped.
    [1, 2, 3, 4].forEach((n) => {
      this.refs[`stepHead${n}`]?.addEventListener('click', () => this.#setActive(n));
    });
    // 'click' rather than 'change': tapping the PRE-SELECTED option must also confirm the
    // step (change never fires on an already-checked radio, which left the default answer
    // with no way forward). Click fires either way and the handler is idempotent.
    // Selection does not auto-advance (Charlie 2026-08-24 round 3: with pre-selected
    // defaults it was not obvious anything had been chosen); every step confirms with Next.
    this.scentRadios.forEach((r) => r.addEventListener('click', () => {
      if (!r.checked) return;
      this.scent = r.value;
      this.render();
      this.#renderSteps();
    }));
    this.refs.scentNext?.addEventListener('click', () => this.#advance(this.#stepAfter(1)));
    const paintRange = () => {
      const r = this.refs.washesRange;
      if (r) r.style.setProperty('--pf', String((this.washes - 1) / Math.max(this.max - 1, 1)));
    };
    paintRange();
    this.refs.washesRange?.addEventListener('input', () => {
      this.washes = Number(this.refs.washesRange.value) || 4;
      if (this.refs.washesRead) this.refs.washesRead.textContent = this.washes >= this.max ? `${this.max}+` : String(this.washes);
      paintRange();
      this.bumped = false;
      this.render();
      this.#renderSteps();
    });
    this.refs.washesNext?.addEventListener('click', () => this.#advance(this.#stepAfter(2)));
    this.loadRadios.forEach((r) => r.addEventListener('click', () => {
      if (!r.checked) return;
      this.factor = Number(r.value) || 1.5;
      this.loadRadios.forEach((x) => x.closest('.dip-pack-option')?.classList.toggle('is-selected', x.checked));
      this.bumped = false;
      this.render();
      this.#renderSteps();
    }));
    this.refs.loadNext?.addEventListener('click', () => this.#advance(this.#stepAfter(3)));
    // The conditioner question: answer enables Next; the plan cards re-price at once.
    this.addonRadios.forEach((r) => r.addEventListener('click', () => {
      if (!r.checked) return;
      this.addonAns = r.value;
      this.addonRadios.forEach((x) => x.closest('.dip-pack-option')?.classList.toggle('is-selected', x.checked));
      if (this.refs.addonNext) this.refs.addonNext.disabled = false;
      this.onceAdded = false;
      this.render();
      this.#renderSteps();
      this.#track('addon', { on: r.value === 'yes' });
    }));
    this.refs.addonNext?.addEventListener('click', () => { if (this.addonAns) this.#advance(PLAN); });

    // The tier block: a tap anywhere on a card selects it; a companion line's checkbox
    // toggles that line (and selects the card); a scent pill re-scents that companion.
    for (let i = 0; this.refs[`tier${i}`]; i += 1) {
      const el = this.refs[`tier${i}`];
      el.addEventListener('click', (e) => {
        if (e.target.closest('.dip-scent-pill')) return;
        if (this.tier !== i) { this.tier = i; this.render(); this.#track('tier'); }
      });
      el.addEventListener('change', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.name === 'dip-plan') {
          if (input.checked && this.tier !== i) { this.tier = i; this.render(); this.#track('tier'); }
        } else if (input.name?.startsWith('dip-comp-scent-')) {
          const key = input.name.slice('dip-comp-scent-'.length);
          this.compScent[key] = input.value;
          this.onceAdded = false;
          this.render();
          this.#refocus(`[ref="tier${i}"] input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`);
        }
      });
    }
    this.refs.bumpLink?.addEventListener('click', () => {
      if (!this.bump) return;
      const bi = this.tiers.findIndex((t) => t.bump);
      if (bi < 0) return;
      this.tier = this.tier === bi ? 0 : bi;
      this.render();
      this.#track('pack', { bumped: this.tier === bi });
    });

    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.#addToCart(true); });
    this.refs.once?.addEventListener('click', () => this.#addToCart(false));
    this.refs.sbCta?.addEventListener('click', () => {
      if (this.choosing && this.refs.evrStep) {
        scrollToStep(this.refs.evrStep);
        return;
      }
      if (this.choice === 'all' && this.evrRoutine) {
        // Everything chosen: add the routine once its plan shows, else walk back to its questions.
        if (this.evrRoutine.isRevealed) { this.evrRoutine.submit(); return; }
        const top = (this.evrRoutine.getBoundingClientRect().top || 0) + window.scrollY - 80;
        window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      if (!this.revealed) {
        // Nothing chosen yet: the floating button walks the shopper back to the questions.
        const top = (this.refs.sflow?.getBoundingClientRect().top || 0) + window.scrollY - 80;
        window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      this.#addToCart(true);
    });
  }

  // After a render rebuilt the line rows, return keyboard focus to the equivalent control so
  // Tab / arrow navigation survives a tick or a scent change.
  #refocus(selector) {
    const el = this.querySelector(selector);
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  }

  // The products whose stock bounds the offer for THIS plan (assets/dip-places.js listens; the
  // keys are also parked on window for a listener that binds after this render).
  #announcePlaces(keys) {
    const list = keys.length ? keys : [this.main.key].filter(Boolean);
    window.__dipPlacesKeys = list;
    document.dispatchEvent(new CustomEvent('dip:places-products', { detail: { keys: list } }));
  }

  // Gate instrumentation (decision doc gates A/B: tier take rate, attach, entry basket). A
  // DOM event plus a dataLayer push, so whichever measurement stack survives the C4 decision
  // can listen without a theme change.
  #track(name, extra = {}) {
    const td = this.chosenTier;
    const detail = {
      surface: 'pdp', product: this.main.key, tier: td?.t?.key || 'basic',
      lines: (td?.on || []).map((l) => l.key), first: td?.firstRaw ?? null, ...extra,
    };
    try {
      document.dispatchEvent(new CustomEvent(`dip:${name}`, { detail, bubbles: true }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `dip_${name}`, ...detail });
    } catch { /* never let telemetry break the buy path */ }
  }

  // ---- Cart guard (assets/dip-msc-guard.js) ------------------------------

  // Publish this surface's hardware model to the cart guard (ruling 9): the MSC page for its
  // own packs, the other PDPs for their "all products" tier. Since ruling-11 fix (b) the
  // hardware is zero-priced included listings and `codes` is empty, so no code is published;
  // the guard still reconciles hardware quantities from the model. One implementation in
  // assets/dip-hardware.js; the store is versioned so retired code entries are wiped.
  #publishGuardConfig() {
    const mscVariants = this.main.isMsc ? (this.model.variants || []) : (this.model.companions?.msc?.variants || []);
    publishHardwareCodes(this.dataset.productId, this.hardware, mscVariants);
  }

  // ---- Cart -------------------------------------------------------------

  #lineItems(subscribe) {
    const c = this.mainCard;
    const items = [];
    if (!subscribe) {
      // One-time = this product alone (the one-time link never carries the tier), plus its
      // hardware on the multi-surface page (product hardware ships either way).
      items.push({ id: Number(c.variant.id), quantity: 1, properties: withArm({ _dip_src: `pdp:${this.main.key}` }) });
      if (this.main.isMsc) items.push(...hwLines(this.hardware, c.variant.size));
      return items;
    }
    const td = this.chosenTier;
    const lines = td ? td.on : [{ variant: c.variant, plan: c.plan, hw: null }];
    lines.forEach((l) => {
      // `_dip_src` (hidden line property, underscore = not rendered in the drawer or Recharge) records the
      // surface the add came from, so attribution can tell a PDP add from a routine-page add. Read by the
      // Monday health scan (Group E); never read by the theme or the cart guard.
      const isMain = l.main || !l.key || l.key === this.main.key;
      const src = isMain ? `pdp:${this.main.key}` : `pdp:${this.main.key}:companion:${l.key}`;
      const props = { _dip_src: src };
      // Half now, half later, arm B: the visible line note the basket and checkout show ("Pay later: £6.00 due 29 Oct").
      const note = hhLineNote(l.price, this.offer, money);
      if (note) props[note.name] = note.value;
      const it = { id: Number(l.variant.id), quantity: 1, properties: withArm(props) };
      if (l.plan?.id) it.selling_plan = Number(l.plan.id);
      items.push(it);
      // Product hardware rides with every multi-surface line, in per-size quantities. Same
      // cart shape as the MSC page, so the BXGY get-quantities (2 on the 1-pack, 4 on the
      // bigger packs) are met exactly.
      if (l.hw?.lines?.length) items.push(...l.hw.lines);
    });
    return items;
  }

  async #addToCart(subscribe) {
    const cta = this.refs.cta;
    this.#clearError();
    // Hardware quantities reconcile against the basket (a 1-pack basket gaining a 2-pack tops
    // up to 2 + 2, never posts a second set): the multi-surface variants of THIS page or of its
    // multi-surface companion tell the helper which cart lines are packs.
    const mscVariants = this.main.isMsc ? (this.model.variants || []) : (this.model.companions?.msc?.variants || []);
    const items = await reconcileHardwareLines(this.#lineItems(subscribe), this.hardware, mscVariants);
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    cta?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Could not add to cart.');
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }
      const itemCount = dipCartCountFromSections(data.sections);
      tagCart();
      this.#announce('Added to your basket');
      if (subscribe && cta) {
        const lbl = this.refs.ctaLabel; const prev = lbl?.textContent;
        if (lbl) { lbl.dataset.busy = '1'; lbl.textContent = '✓ Added to basket'; }
        setTimeout(() => { if (lbl) { delete lbl.dataset.busy; lbl.textContent = prev; } }, 1600);
      }
      if (!subscribe) { this.onceAdded = true; this.render(); setTimeout(() => { this.onceAdded = false; this.render(); }, 2400); }
      this.#track('add', { subscribe, items: items.length });
      this.dispatchEvent(new CartAddEvent(undefined, this.id || 'dip-simple-buy', {
        source: 'dip-simple-buy', itemCount: itemCount ?? items.length, productId: this.dataset.productId,
        variantId: this.variant.id, sections: data.sections,
      }));
    } catch (err) {
      console.error('[dip-simple-buy] add to cart failed', err);
      this.#showError('Something went wrong. Please try again.');
    } finally {
      cta?.removeAttribute('aria-busy');
    }
  }

  #showError(msg) { if (this.refs.error) { this.refs.error.textContent = msg; this.refs.error.hidden = false; } }
  #clearError() { if (this.refs.error) this.refs.error.hidden = true; }
  #announce(msg) { if (this.refs.liveRegion) this.refs.liveRegion.textContent = msg; }

  // ---- Peripheral wiring (same shapes as dip-buy-box.js) -----------------

  #initGallery() {
    initGallery({ track: this.refs.gallery, dots: this.refs.dots, thumbs: this.refs.thumbs, label: 'image' });
  }

  #initCarousel() {
    const track = this.refs.vidTrack, next = this.refs.vidNext;
    next?.addEventListener('click', () => track?.scrollBy({ left: Math.round(track.clientWidth * 0.8), behavior: 'smooth' }));
    this.querySelectorAll('.dip-vid-thumb[data-video]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const src = btn.dataset.video;
        if (!src || btn.dataset.playing) return;
        const v = document.createElement('video');
        v.src = src; v.controls = true; v.autoplay = true; v.playsInline = true; v.className = 'dip-vid-el';
        btn.appendChild(v); btn.dataset.playing = '1';
        btn.querySelector('.dip-vid-play')?.style.setProperty('display', 'none');
      });
    });
  }

  #initTabsets() {
    this.querySelectorAll('[data-dip-tabs]').forEach((set) => {
      const tabs = [...set.querySelectorAll('[role="tab"]')];
      const panels = tabs.map((t) => set.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`));
      const select = (n) => {
        const idx = (n + tabs.length) % tabs.length;
        tabs.forEach((t, ti) => {
          const on = ti === idx;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (panels[ti]) { panels[ti].hidden = !on; panels[ti].classList.toggle('is-hidden', !on); }
        });
      };
      tabs.forEach((t, ti) => {
        t.addEventListener('click', () => select(ti));
        t.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          const next = (ti + dir + tabs.length) % tabs.length;
          select(next); tabs[next].focus();
        });
      });
    });
  }

  #initHowLink() {
    const link = this.refs.howLink, modal = this.refs.subModal;
    if (!link) return;
    if (!modal) { link.hidden = true; return; }
    const supportsModal = typeof modal.showModal === 'function';
    const open = () => { supportsModal ? modal.showModal() : modal.setAttribute('open', ''); };
    const close = () => { supportsModal ? modal.close() : modal.removeAttribute('open'); };
    link.addEventListener('click', open);
    this.refs.subModalClose?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  }

  // Amazon-style ratings popover on the Trustpilot strip: stars toggle the star-breakdown
  // card; its link jumps to the reviews hub. Closes on outside click and Escape.
  #initTpPop() {
    const btn = this.refs.tpToggle, pop = this.refs.tpPop;
    if (!btn || !pop) return;
    const close = () => { pop.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
    btn.addEventListener('click', () => {
      const open = pop.hidden;
      pop.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    this.refs.tpPopLink?.addEventListener('click', close);
    document.addEventListener('click', (e) => { if (!pop.hidden && !e.target.closest('.dip-tp-wrap')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  #initSticky() {
    this.stickyBarEl = initStickyBar({ bar: this.refs.stickybar, anchor: this.refs.sflow || this.querySelector('.dip-media') });
  }
}

if (!customElements.get('dip-simple-buy')) customElements.define('dip-simple-buy', DipSimpleBuy);

// Reveal-on-scroll - same block as dip-buy-box.js / dip-home.js / dip-pdp-below.js;
// the flag makes the first one to run the only one that binds.
(() => {
  if (window.__dipRevealInit) return;
  window.__dipRevealInit = true;
  const reveal = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries, obs) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); obs.unobserve(e.target); } }),
      { rootMargin: '0px 0px -4% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.dip-reveal').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!(r.top < window.innerHeight && r.bottom > 0)) { el.classList.add('dip-pre'); io.observe(el); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal, { once: true });
  else reveal();
})();
